import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { PublicJobcardView, LineKind, JobcardStatus } from '@garage/shared'
import { PUBLIC_WORK_LABELS, PUBLIC_STATUS_HINTS } from '@garage/shared'
import { publicApi, PublicApiError } from '@/lib/public-api.js'
import { makeFormatters } from '@/lib/format.js'
import {
  Button,
  Card,
  ConfirmDialog,
  KeyValue,
  Skeleton,
  DownloadIcon,
  VisibilityIcon,
  WhatsappIcon,
  useSnackbar,
} from '@/ui'
import { LanguageToggle } from '@/components/LanguageToggle.js'
import { PublicInspectionSection } from '@/components/public/PublicInspectionSection.js'

/**
 * Customer-facing portal page showing a single jobcard.
 *
 * Opened outside the app shell: no sidebar, no navigation into the staff app.
 * Renders at `/p/:token` when the server exposes the public portal.
 */
export function PublicJobcardPage() {
  const { token } = useParams<{ token: string }>()
  const [view, setView] = useState<PublicJobcardView | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!token) {
      setError('invalid')
      setIsLoading(false)
      return
    }

    const load = async () => {
      try {
        setIsLoading(true)
        const data = await publicApi.jobcard(token)
        setView(data)
        setError(null)
      } catch (err) {
        if (err instanceof PublicApiError && err.status === 404) {
          // A 404 has two very different causes and only one of them is the
          // customer's problem. The staff app serves this same bundle, so a
          // portal link pointed at the STAFF address loads the page fine and
          // then 404s, because /api/public is registered only on the portal
          // listener. Telling that customer their link expired is a lie, and
          // it sends them back to the garage for a new link that will fail in
          // exactly the same way.
          setError((await isStaffListener()) ? 'wrong-address' : 'invalid')
        } else {
          setError('network')
        }
      } finally {
        setIsLoading(false)
      }
    }

    load()
  }, [token])

  if (isLoading) {
    return <LoadingSkeleton />
  }

  if (error || !view) {
    return <ErrorPage reason={error} garagePhone={view?.garage.phone ?? null} />
  }

  const formatters = makeFormatters({
    currencySymbol: view.totals.currencySymbol,
    currencyDecimals: view.totals.currencyDecimals,
    dateFormat: 'dd/MM/yyyy',
  } as any)

  return (
    // html/body/#root are globally clamped to height:100%/overflow:hidden for
    // the staff app's own single scrolling region (AppShell's <main>) — this
    // page renders outside AppShell entirely, so it needs to BE that region
    // itself rather than relying on the document to scroll.
    <div className="h-full overflow-y-auto overscroll-y-contain bg-background text-on-surface">
      <div className="mx-auto w-full max-w-md px-4 py-6 sm:max-w-none sm:px-6 lg:px-8">
        <div className="flex justify-end mb-2">
          <LanguageToggle />
        </div>
        <GarageHeader garage={view.garage} />
        <div className="mt-8 space-y-6">
          <StatusSection jobcard={view.jobcard} />

          {/*
            Everything the customer can DO, directly under the status.
            Previously all three sat at the foot of the page, below the car,
            the work and the totals — so paying an invoice meant scrolling past
            everything first, on the one screen where the action is the point.
            Ordered by what is most wanted: settle it, read it, ask about it.
          */}
          <div className="space-y-3">
            <QuotationDecisionAction
              token={token}
              awaitingApproval={view.jobcard.awaitingApproval}
              onDecided={setView}
            />
            <PayAction
              payment={view.payment}
              amountDueMinor={view.totals.amountDueMinor}
              formatters={formatters}
            />
            <DocumentsSection
              documents={view.documents}
              token={token}
              formatters={formatters}
            />
            <WhatsappAction garage={view.garage} jobcardNumber={view.jobcard.number} />
          </div>

          <CarSection car={view.car} />
          <WorkSection lines={view.lines} complaint={view.jobcard.complaint} />
          <TotalsSection totals={view.totals} formatters={formatters} />
          {view.inspection && token && (
            <PublicInspectionSection token={token} inspection={view.inspection} view={view} />
          )}
          <BankDetails payment={view.payment} />
          <ContactDetails garage={view.garage} />
        </div>
      </div>
    </div>
  )
}

/**
 * Garage header with logo (optional) and name.
 */
function GarageHeader({ garage }: { garage: PublicJobcardView['garage'] }) {
  return (
    <div className="text-center">
      {garage.hasLogo && (
        <img
          src={`/api/public/logo`}
          alt={garage.name}
          className="mx-auto h-16 w-auto mb-3"
          onError={(e) => {
            // If logo fails to load, hide it
            e.currentTarget.style.display = 'none'
          }}
        />
      )}
      <h1 className="text-headline-md text-on-surface">{garage.name}</h1>
    </div>
  )
}

/**
 * Status section: large status label, hint, dot, and jobcard number.
 */
/**
 * The colour sits on the jobcard-number strip at the foot of the status card.
 *
 * This is the first thing on the page and usually the only thing a customer
 * came to read — is it ready, do I owe anything. A 6px dot is a footnote on a
 * phone held at arm's length, so the panel says it instead. Confining the
 * colour to the strip keeps the status wording on a plain ground, where long
 * labels stay easy to read, and still gives the card one unmissable band.
 *
 * Written out per tone rather than composed from a template string, because
 * Tailwind scans source text for class names and cannot see one that is built
 * at runtime — `bg-${tone}-container` compiles and then renders unstyled.
 *
 * `neutral` stays plain on purpose. An ordinary state should not shout, or the
 * states that matter stop standing out.
 */
const STATUS_STRIP: Record<
  PublicJobcardView['jobcard']['statusTone'],
  { strip: string; label: string; value: string }
> = {
  neutral: {
    strip: 'bg-surface-high',
    label: 'text-on-surface-variant',
    value: 'text-on-surface',
  },
  info: {
    strip: 'bg-info-container',
    label: 'text-on-info-container',
    value: 'text-on-info-container',
  },
  warning: {
    strip: 'bg-warning-container',
    label: 'text-on-warning-container',
    value: 'text-on-warning-container',
  },
  violet: {
    strip: 'bg-violet-container',
    label: 'text-on-violet-container',
    value: 'text-on-violet-container',
  },
  teal: {
    strip: 'bg-teal-container',
    label: 'text-on-teal-container',
    value: 'text-on-teal-container',
  },
  success: {
    strip: 'bg-success-container',
    label: 'text-on-success-container',
    value: 'text-on-success-container',
  },
  error: {
    strip: 'bg-error-container',
    label: 'text-on-error-container',
    value: 'text-on-error-container',
  },
}

/**
 * The server sends only the resolved English text (workLabel/paymentLabel/
 * statusHint), not the underlying status enum, so the customer-facing wording
 * is looked up by translating the STATUS these fixed English strings came
 * from — reversing PUBLIC_WORK_LABELS/PUBLIC_STATUS_HINTS back to a status key.
 * Two statuses ('completed' and 'invoiced') share the same English work label
 * ("Ready"), but since both translate to the same text anyway, either match
 * is correct.
 */
const WORK_LABEL_STATUS_BY_TEXT: Record<string, JobcardStatus> = Object.fromEntries(
  Object.entries(PUBLIC_WORK_LABELS).map(([status, text]) => [text, status as JobcardStatus]),
)
const STATUS_HINT_STATUS_BY_TEXT: Record<string, JobcardStatus> = Object.fromEntries(
  Object.entries(PUBLIC_STATUS_HINTS).map(([status, text]) => [text, status as JobcardStatus]),
)
const PAYMENT_LABEL_KEY_BY_TEXT: Record<string, string> = {
  'Nothing to pay': 'nothingToPay',
  'Paid in full': 'paidInFull',
  'Balance due': 'balanceDue',
  'Payment due': 'paymentDue',
  'Deposit received': 'depositReceived',
  'Nothing to pay yet': 'nothingToPayYet',
}

function StatusSection({ jobcard }: { jobcard: PublicJobcardView['jobcard'] }) {
  const { t } = useTranslation()
  const tone = STATUS_STRIP[jobcard.statusTone] ?? STATUS_STRIP.neutral

  const workStatus = WORK_LABEL_STATUS_BY_TEXT[jobcard.workLabel]
  const workLabel = workStatus ? t(`publicStatus.workLabels.${workStatus}`, jobcard.workLabel) : jobcard.workLabel

  const hintStatus = STATUS_HINT_STATUS_BY_TEXT[jobcard.statusHint]
  const statusHint = hintStatus ? t(`publicStatus.hints.${hintStatus}`, jobcard.statusHint) : jobcard.statusHint

  const paymentKey = PAYMENT_LABEL_KEY_BY_TEXT[jobcard.paymentLabel]
  const paymentLabel = paymentKey ? t(`publicStatus.paymentLabels.${paymentKey}`, jobcard.paymentLabel) : jobcard.paymentLabel

  return (
    <Card variant="filled" padded={false}>
      <div className="p-5 pb-4">
        {/*
          Two lines, because the customer is usually asking one of two
          questions — is my car ready, and do I owe anything — and a single
          "Ready — awaiting payment" makes them read a sentence to find the
          half they came for. Split, each is a glance.
        */}
        <h2 className="text-figure-lg text-on-surface">{workLabel}</h2>
        <p className="text-title-md text-on-surface-variant mb-2">{paymentLabel}</p>
        <p className="text-body-sm text-on-surface-variant">{statusHint}</p>
      </div>
      {/*
        Full-bleed to the card's own rounded corners, so it reads as part of the
        card rather than a box floating inside one.
      */}
      <div
        className={`flex items-center justify-between px-5 py-3 rounded-b-[inherit] ${tone.strip}`}
      >
        <span className={`text-label-sm ${tone.label}`}>{t('publicPortal.jobcard')}</span>
        <span className={`ref text-body-md ${tone.value}`}>{jobcard.number}</span>
      </div>
    </Card>
  )
}

/**
 * Car section: brand, model, year, plate, colour.
 */
function CarSection({ car }: { car: PublicJobcardView['car'] }) {
  const { t } = useTranslation()
  return (
    <Card variant="filled" className="space-y-2">
      <KeyValue
        label={t('carForm.brand')}
        value={car.brand}
        numeric={false}
      />
      <KeyValue
        label={t('carForm.model')}
        value={car.model}
        numeric={false}
      />
      {car.year && (
        <KeyValue
          label={t('carForm.year')}
          value={car.year}
          numeric={false}
        />
      )}
      <KeyValue
        label={t('carForm.plate')}
        value={<span className="ref">{car.plate}</span>}
        numeric={false}
      />
      {car.color && (
        <KeyValue
          label={t('carForm.colour')}
          value={car.color}
          numeric={false}
        />
      )}
    </Card>
  )
}

/**
 * Work section: lines grouped by kind, with optional complaint above.
 */
function WorkSection({
  lines,
  complaint,
}: {
  lines: PublicJobcardView['lines']
  complaint: string | null
}) {
  const { t, i18n } = useTranslation()

  // Group lines by kind
  const linesByKind = new Map<LineKind, typeof lines>()
  for (const line of lines) {
    if (!linesByKind.has(line.kind)) {
      linesByKind.set(line.kind, [])
    }
    linesByKind.get(line.kind)!.push(line)
  }

  const kindNames: Record<LineKind, string> = {
    service: t('publicPortal.kindNames.service'),
    part: t('publicPortal.kindNames.part'),
    material: t('publicPortal.kindNames.material'),
  }

  return (
    <div className="space-y-4">
      {complaint && (
        <Card variant="filled">
          <div className="space-y-1">
            <h3 className="text-label-lg text-on-surface">{t('publicPortal.whatYouToldUs')}</h3>
            <p className="text-body-sm text-on-surface-variant">{complaint}</p>
          </div>
        </Card>
      )}

      {Array.from(linesByKind.entries()).map(([kind, kindLines]) => (
        <Card key={kind} variant="filled">
          <h3 className="text-title-md text-on-surface mb-3 pb-2 border-b border-outline-variant">
            {kindNames[kind]}
          </h3>
          <div className="space-y-2">
            {kindLines.map((line, idx) => (
              <div key={idx} className="space-y-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-body-sm text-on-surface flex-1">{line.name}</span>
                  {line.partNumber && (
                    <span className="ref text-label-sm text-on-surface-variant">
                      {line.partNumber}
                    </span>
                  )}
                </div>
                <div className="flex items-baseline justify-between gap-4 text-body-sm">
                  <span className="text-on-surface-variant">
                    {(line.quantityMilli / 1000).toFixed(line.quantityMilli % 1000 === 0 ? 0 : 1)} ×{' '}
                    <span className="text-on-surface">{formatMinorAsAmount(line.rateMinor, i18n.language)}</span>
                  </span>
                  <span className="tabular text-on-surface">
                    {formatMinorAsAmount(line.lineTotalMinor, i18n.language)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  )
}

/**
 * Totals section: subtotal, discount, VAT, total.
 */
function TotalsSection({
  totals,
  formatters,
}: {
  totals: PublicJobcardView['totals']
  formatters: ReturnType<typeof makeFormatters>
}) {
  const { t } = useTranslation()
  return (
    <Card variant="filled" className="space-y-2">
      <KeyValue
        label={t('totalsBar.subtotal')}
        value={formatters.amount(totals.subtotalMinor)}
        numeric
      />

      {totals.discountMinor !== 0 && (
        <KeyValue
          label={t('totalsBar.discount')}
          value={`−${formatters.amount(Math.abs(totals.discountMinor))}`}
          numeric
        />
      )}

      {totals.vatEnabled && (
        <KeyValue
          label={`${totals.vatLabel} (${(totals.vatRateBp / 100).toFixed(1)}%)`}
          value={formatters.amount(totals.vatMinor)}
          numeric
        />
      )}

      <KeyValue
        label={t('dashboard.columns.total')}
        value={formatters.money(totals.totalMinor)}
        numeric
        total
      />

      {/*
        The deposit, and what is left. Shown only when there is one, so an
        ordinary job keeps a three-line total rather than growing two rows
        that both say the same number.
      */}
      {totals.amountPaidMinor > 0 && (
        <>
          <KeyValue
            label={t('publicPortal.depositPaid')}
            value={`−${formatters.amount(totals.amountPaidMinor)}`}
            numeric
          />
          <KeyValue
            label={t('totalsBar.amountDue')}
            value={formatters.money(totals.amountDueMinor)}
            numeric
            total
          />
        </>
      )}
    </Card>
  )
}

/**
 * Documents section: quotation and invoice rows.
 */
function DocumentsSection({
  documents,
  token,
  formatters,
}: {
  documents: PublicJobcardView['documents']
  token: string | undefined
  formatters: ReturnType<typeof makeFormatters>
}) {
  const { t } = useTranslation()
  if (!documents.length) return null

  const getDocTypeLabel = (type: 'quotation' | 'invoice') =>
    type === 'quotation' ? t('jobcardEditor.quotationWord') : t('jobcardEditor.invoiceWord')

  return (
    <Card variant="filled" className="space-y-2">
      {documents.map((doc) => {
        const docTypeLabel = getDocTypeLabel(doc.type)
        const previewUrl = token ? publicApi.documentPdfUrl(token, doc.id) : undefined
        const downloadUrl = token ? publicApi.documentPdfDownloadUrl(token, doc.id) : undefined

        return (
          <div key={doc.id} className="flex items-center justify-between gap-2 py-2">
            {/* Preview link: type, number, date as a clickable link */}
            <a
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 min-w-0 hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:rounded"
              aria-label={t('publicPortal.previewDoc', { type: docTypeLabel })}
              onClick={(e) => {
                if (!token) e.preventDefault()
              }}
            >
              <div className="text-body-sm text-on-surface capitalize flex items-center gap-1.5">
                <VisibilityIcon className="h-4 w-4 flex-shrink-0" aria-hidden />
                {docTypeLabel}
              </div>
              <div className="flex items-baseline gap-2 mt-0.5">
                <span className="ref text-label-sm text-on-surface-variant">{doc.number}</span>
                <span className="text-label-sm text-on-surface-subtle">
                  {formatters.date(doc.issuedAt)}
                </span>
              </div>
            </a>

            {/* Right side: total and download button */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="tabular text-body-sm text-on-surface whitespace-nowrap">
                {formatters.money(doc.totalMinor)}
              </span>
              <Button
                variant="outlined"
                size="sm"
                icon={<DownloadIcon />}
                onClick={() => {
                  if (!downloadUrl) return
                  const a = document.createElement('a')
                  a.href = downloadUrl
                  a.download = `${doc.type}-${doc.number}.pdf`
                  document.body.appendChild(a)
                  a.click()
                  document.body.removeChild(a)
                }}
                aria-label={t('publicPortal.downloadDoc', {
                  type: docTypeLabel,
                })}
                disabled={!token}
              />
            </div>
          </div>
        )
      })}
    </Card>
  )
}

/**
 * Approve/decline the quotation currently awaiting the customer's approval.
 *
 * Absent for every other status: once a decision lands the server response
 * comes back with `awaitingApproval: false` (it moved to 'in_progress' or
 * back to 'draft'), and `onDecided` swaps in that fresh view — so the buttons
 * disappear on their own, with no separate reload.
 */
function QuotationDecisionAction({
  token,
  awaitingApproval,
  onDecided,
}: {
  token: string | undefined
  awaitingApproval: boolean
  onDecided: (view: PublicJobcardView) => void
}) {
  const snackbar = useSnackbar()
  const { t } = useTranslation()
  const [pending, setPending] = useState<'approve' | 'decline' | null>(null)
  const [confirmingDecline, setConfirmingDecline] = useState(false)

  if (!awaitingApproval) return null

  const decide = async (decision: 'approve' | 'decline') => {
    if (!token) return
    setPending(decision)
    try {
      const updated = await publicApi.decideQuotation(token, { decision })
      onDecided(updated)
      snackbar.success(
        decision === 'approve'
          ? t('publicPortal.thanksApproved')
          : t('publicPortal.thanksDeclined'),
      )
    } catch {
      snackbar.error(t('publicPortal.couldNotRecordDecision'))
    } finally {
      setPending(null)
      setConfirmingDecline(false)
    }
  }

  return (
    <Card variant="filled" className="space-y-3">
      <div>
        <h3 className="text-label-lg text-on-surface">{t('publicPortal.approveThisQuotation')}</h3>
        <p className="text-body-sm text-on-surface-variant mt-1">
          {t('publicPortal.letUsKnow')}
        </p>
      </div>
      <div className="flex gap-3">
        <Button
          variant="filled"
          fullWidth
          onClick={() => decide('approve')}
          loading={pending === 'approve'}
          disabled={pending !== null}
        >
          {t('publicPortal.approve')}
        </Button>
        <Button
          variant="outlined"
          fullWidth
          onClick={() => setConfirmingDecline(true)}
          disabled={pending !== null}
        >
          {t('publicPortal.decline')}
        </Button>
      </div>

      <ConfirmDialog
        open={confirmingDecline}
        title={t('publicPortal.declineThisQuotation')}
        message={t('publicPortal.declineMessage')}
        confirmLabel={t('publicPortal.decline')}
        destructive
        loading={pending === 'decline'}
        onConfirm={() => decide('decline')}
        onCancel={() => setConfirmingDecline(false)}
      />
    </Card>
  )
}

/**
 * The Pay button.
 *
 * Split from the bank details it used to sit with, because the two want to be
 * in different places: paying is an action and belongs at the top with the
 * others, while account numbers are reference material nobody reads until they
 * need them.
 *
 * The "payment received" card is gone: the status card is green and says "Paid
 * — thank you" three lines above, and a second panel repeating it was noise.
 */
function PayAction({
  payment,
  amountDueMinor,
  formatters,
}: {
  payment: PublicJobcardView['payment']
  amountDueMinor: number
  formatters: ReturnType<typeof makeFormatters>
}) {
  const { t } = useTranslation()
  if (payment.state !== 'awaiting_payment') return null

  /*
   * Only ever navigate to http(s). The link is free text typed by staff and
   * this is the one internet-facing page in the app, so a `javascript:` value
   * would run in the portal's own origin. The schema now blocks such a value
   * at the source; this guards rows saved before that rule existed.
   */
  const safePaymentUrl =
    payment.paymentUrl && /^https?:\/\//i.test(payment.paymentUrl) ? payment.paymentUrl : null

  if (!safePaymentUrl) return null

  return (
    // A real link, not a scripted navigation: it gets normal browser
    // behaviour, and `noopener` keeps the payment page from touching this
    // one through window.opener.
    <a
      href={safePaymentUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="block w-full text-center bg-primary text-on-primary rounded-full h-11 leading-[2.75rem] text-label-lg interactive hover:opacity-90"
    >
      {t('publicPortal.payNow', { amount: formatters.money(amountDueMinor) })}
    </a>
  )
}

/** Where to send the money. Reference material, so it stays near the bottom. */
function BankDetails({ payment }: { payment: PublicJobcardView['payment'] }) {
  const { t } = useTranslation()
  if (payment.state !== 'awaiting_payment' || !payment.bankDetails) return null

  return (
    <Card variant="filled">
      <div className="space-y-1">
        <h3 className="text-label-lg text-on-surface mb-2">{t('settings.bankDetails')}</h3>
        <p className="text-body-sm text-on-surface whitespace-pre-wrap">{payment.bankDetails}</p>
      </div>
    </Card>
  )
}

/**
 * Ask the workshop a question.
 *
 * Split from the address block for the same reason as the Pay button: this is
 * an action and belongs with the others, while a phone number and an address
 * are reference material.
 */
function WhatsappAction({
  garage,
  jobcardNumber,
}: {
  garage: PublicJobcardView['garage']
  jobcardNumber: string
}) {
  const { t } = useTranslation()
  if (!garage.whatsappDigits) return null

  const whatsappMessage = t('publicPortal.enquiringAbout', { jobcard: jobcardNumber })

  return (
    <Button
      variant="outlined"
      size="lg"
      fullWidth
      icon={<WhatsappIcon className="h-5 w-5" />}
      onClick={() => {
        const url = `https://wa.me/${garage.whatsappDigits}?text=${encodeURIComponent(whatsappMessage)}`
        window.location.href = url
      }}
    >
      {t('publicPortal.messageOnWhatsapp')}
    </Button>
  )
}

/** Where the workshop is, for when somebody wants to drive there. */
function ContactDetails({ garage }: { garage: PublicJobcardView['garage'] }) {
  const { t } = useTranslation()
  return (
    <div className="space-y-4">
      <Card variant="filled" className="space-y-2">
        {garage.phone && (
          <KeyValue
            label={t('clients.columns.phone')}
            value={garage.phone}
            numeric={false}
          />
        )}
        <KeyValue
          label={t('publicPortal.address')}
          value={garage.address}
          numeric={false}
        />
      </Card>
    </div>
  )
}

/**
 * Loading skeleton while the jobcard is fetching.
 */
function LoadingSkeleton() {
  return (
    <div className="h-full overflow-y-auto overscroll-y-contain bg-background text-on-surface">
      <div className="mx-auto w-full max-w-md px-4 py-6 sm:max-w-none sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <Skeleton className="mx-auto w-16 h-4 mb-3" />
          <Skeleton lines={1} />
        </div>

        <div className="space-y-6">
          {/* Status skeleton */}
          <div className="bg-surface-container border border-outline-variant rounded-lg p-5">
            <Skeleton lines={3} />
          </div>

          {/* Car skeleton */}
          <div className="bg-surface-container border border-outline-variant rounded-lg p-5">
            <Skeleton lines={4} />
          </div>

          {/* Work skeleton */}
          <div className="bg-surface-container border border-outline-variant rounded-lg p-5">
            <Skeleton lines={5} />
          </div>

          {/* Totals skeleton */}
          <div className="bg-surface-container border border-outline-variant rounded-lg p-5">
            <Skeleton lines={4} />
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Is this bundle being served by the STAFF listener rather than the portal?
 *
 * The two listeners serve the same build, and the difference is which routes
 * exist: /api/settings is registered only on the staff app, /api/public only
 * on the portal. Asking for the staff-only route is therefore a direct test of
 * which port answered, and it leaks nothing — a 200 here means the caller was
 * already on the staff app, where they could have read it anyway.
 */
async function isStaffListener(): Promise<boolean> {
  try {
    const res = await fetch('/api/settings', { method: 'GET' })
    return res.ok
  } catch {
    return false
  }
}

/**
 * Error page when the link is invalid or the jobcard cannot be loaded.
 */
function ErrorPage({ reason, garagePhone }: { reason: string | null; garagePhone: string | null }) {
  const { t } = useTranslation()

  if (reason === 'wrong-address') {
    return (
      <div className="h-full overflow-y-auto overscroll-y-contain bg-background text-on-surface flex items-center justify-center p-4">
        <div className="max-w-md space-y-4">
          <div className="flex justify-end">
            <LanguageToggle />
          </div>
          <h1 className="text-headline-md text-on-surface">{t('publicPortal.wrongAddressTitle')}</h1>
          <p className="text-body-md text-on-surface-variant">
            {t('publicPortal.wrongAddressBody')}
          </p>
          <p className="text-body-sm text-on-surface-variant">
            {t('publicPortal.wrongAddressStaffPrefix')}{' '}
            <span className="ref">{t('publicPortal.wrongAddressStaffPath')}</span>{' '}
            {t('publicPortal.wrongAddressStaffSuffix')}
          </p>
        </div>
      </div>
    )
  }

  if (reason === 'network') {
    return (
      <div className="h-full overflow-y-auto overscroll-y-contain bg-background text-on-surface flex items-center justify-center p-4">
        <div className="max-w-md text-center space-y-4">
          <div className="flex justify-end">
            <LanguageToggle />
          </div>
          <h1 className="text-headline-md text-on-surface">{t('publicPortal.couldNotReachGarage')}</h1>
          <p className="text-body-md text-on-surface-variant">
            {t('publicPortal.couldNotReachGarageBody')}
          </p>
          {garagePhone && (
            <div className="pt-4 border-t border-outline-variant">
              <p className="text-body-sm text-on-surface-variant">{t('publicPortal.garagePhone')}</p>
              <a
                href={`tel:${garagePhone}`}
                className="text-primary hover:underline font-semibold text-body-md block mt-2"
              >
                {garagePhone}
              </a>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto overscroll-y-contain bg-background text-on-surface flex items-center justify-center p-4">
      <div className="max-w-md text-center space-y-4">
        <div className="flex justify-end">
          <LanguageToggle />
        </div>
        <h1 className="text-headline-md text-on-surface">{t('publicPortal.linkNoLongerValid')}</h1>
        <p className="text-body-md text-on-surface-variant">
          {t('publicPortal.contactGarageForLink')}
        </p>
        {garagePhone && (
          <div className="pt-4 border-t border-outline-variant">
            <p className="text-body-sm text-on-surface-variant">{t('publicPortal.garagePhone')}</p>
            <a
              href={`tel:${garagePhone}`}
              className="text-primary hover:underline font-semibold text-body-md block mt-2"
            >
              {garagePhone}
            </a>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Format a minor value as an amount without currency symbol.
 */
function formatMinorAsAmount(minor: number, language: string): string {
  return (minor / 100).toLocaleString(language === 'ar' ? 'ar-u-nu-latn' : 'en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}
